import React from 'react';
import { sanitize } from '../utils/sanitize';

/**
 * TalkingPoints — the single, shared renderer for a lead/customer's
 * `talking_points` string.
 *
 * The backend stores talking points as one free-form LLM string whose markdown
 * shape varies run to run (`* `, `• `, `- `, `1.`, `**bold**:`, or just prose).
 * Previously AgentBriefing and LeadDetailPanel each had their own ~70-line
 * inline parser, so the same data rendered differently in each place. This
 * component normalizes every variant into one consistent checklist so a rep
 * sees the same practical format everywhere.
 *
 * Checking an item is a local, ephemeral aid for working through a visit — it
 * is intentionally not persisted.
 */

// Split a raw talking-points string into clean, display-ready point strings.
export const parseTalkingPoints = (raw) => {
  // Drop markdown bold emphasis FIRST so the `**` in `**Topic**:` is never
  // mistaken for a `*` bullet marker (which would tear the heading apart).
  const text = sanitize(raw).replace(/\*\*/g, '').trim();
  if (!text) return [];

  const hasStarOrDot = /[*•]/.test(text);
  const hasDashOrNum = /(^|\n)\s*(?:[-–]\s|\d+[.)]\s)/.test(text);

  let segments;
  if (hasStarOrDot) {
    // Single `*` or `•` bullets — may appear inline on one line.
    segments = text.split(/\s*[*•]\s*/);
  } else if (hasDashOrNum) {
    // Dash or numbered lists — markers sit at the start of each line.
    segments = text.split(/(?:^|\n)\s*(?:[-–]\s|\d+[.)]\s)/);
  } else {
    // No markers — one point per line; a single block stays one point
    // rather than being chopped mid-sentence.
    segments = text.split(/\n+/);
  }

  const points = segments
    .map((s) => s.replace(/^[*•\-–\s]+/, '').replace(/^\d+[.)]\s*/, '').trim())
    .filter(Boolean);

  // A leading "Here's what to cover:"-style lead-in (ends with a colon, isn't
  // itself a talking point) is noise in a checklist — drop it when it precedes
  // real points.
  if (points.length > 1 && /:$/.test(points[0]) && points[0].length < 80) {
    return points.slice(1);
  }
  return points;
};

// Render one point, promoting a leading "Topic:" or "Topic —" into bold.
const PointText = ({ point, headerClass }) => {
  const m = point.match(/^([^:–—]{1,40})\s*[:–—]\s+(.*)$/s);
  if (m) {
    return (
      <>
        <span className={`font-semibold ${headerClass}`}>{m[1].trim()}</span>
        <span> — {m[2].trim()}</span>
      </>
    );
  }
  return <>{point}</>;
};

const TalkingPoints = ({
  text,
  // Theming so the checklist sits naturally in each context (briefing vs panel)
  // while keeping identical structure/typography.
  accent = 'blue',          // 'blue' (briefing) | 'amber' (detail panel)
  panelMode = true,
}) => {
  const points = parseTalkingPoints(text);

  if (points.length === 0) return null;

  // Fully-spelled class names per accent so Tailwind's JIT scanner picks them up
  // (constructing class strings at runtime would silently drop the styles).
  const dotColor = accent === 'amber' ? 'bg-amber-400' : 'bg-blue-400';
  const baseText = panelMode ? 'text-gray-300' : 'text-gray-700';
  const headerText = panelMode ? 'text-gray-100' : 'text-gray-900';

  return (
    <ul className="space-y-2" data-testid="talking-points">
      {points.map((point, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className={`mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${dotColor}`} />
          <span className={`text-sm leading-relaxed ${baseText}`}>
            <PointText point={point} headerClass={headerText} />
          </span>
        </li>
      ))}
    </ul>
  );
};

export default TalkingPoints;
